import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';

export default function AdminPlanSettings() {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newPlan, setNewPlan] = useState({
    key: '', name: '', price: 0,
    max_stores: 1, max_branches: 1, max_products: 20,
    custom_domain: false, free_domain_suffix: 'cartz.cc',
    pos_enabled: false,
  });
  const [adding, setAdding] = useState(false);

  const loadPlans = () => {
    api.get('/admin/plan-settings').then((res) => {
      setPlans(res.data);
      setLoading(false);
    });
  };

  useEffect(() => { loadPlans(); }, []);

  const updateField = (id: number, field: string, value: any) => {
    setPlans((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));
  };

  /** Allow clearing number inputs; empty becomes 0 on save. */
  const parseOptionalInt = (raw: string): number | '' =>
    raw === '' ? '' : (Number.isNaN(parseInt(raw, 10)) ? '' : parseInt(raw, 10));

  const toInt = (v: number | '' | null | undefined, fallback = 0) =>
    v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? fallback : Number(v);

  const savePlan = async (plan: any) => {
    setSaving(plan.id);
    try {
      await api.put(`/admin/plan-settings/${plan.id}`, {
        name: plan.name,
        price: plan.price === '' || plan.price == null ? 0 : Number(plan.price),
        max_stores: toInt(plan.max_stores),
        max_branches: toInt(plan.max_branches),
        max_products: toInt(plan.max_products),
        custom_domain: plan.custom_domain,
        pos_enabled: plan.pos_enabled,
        free_domain_suffix: plan.free_domain_suffix,
        is_active: plan.is_active,
      });
      toast.success(`${plan.name} plan saved!`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const handleAddPlan = async () => {
    if (!newPlan.key.trim() || !newPlan.name.trim()) {
      toast.error('Key and Name are required');
      return;
    }
    setAdding(true);
    try {
      await api.post('/admin/plan-settings', {
        ...newPlan,
        price: toInt(newPlan.price as any, 0),
        max_stores: toInt(newPlan.max_stores),
        max_branches: toInt(newPlan.max_branches),
        max_products: toInt(newPlan.max_products),
      });
      toast.success('Plan created!');
      setShowAdd(false);
      setNewPlan({ key: '', name: '', price: 0, max_stores: 1, max_branches: 1, max_products: 20, custom_domain: false, free_domain_suffix: 'cartz.cc', pos_enabled: false });
      loadPlans();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create plan');
    } finally {
      setAdding(false);
    }
  };

  const handleDeletePlan = async (plan: any) => {
    if (!window.confirm(`Delete "${plan.name}" plan? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/plan-settings/${plan.id}`);
      setPlans((prev) => prev.filter((p) => p.id !== plan.id));
      toast.success('Plan deleted');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Plan Settings</h1>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          <FiPlus style={{ marginInlineEnd: 6 }} /> Add Plan
        </button>
      </div>
      <p style={{ margin: '0 0 24px', color: 'var(--gray-500)', fontSize: 14 }}>
        Configure limits and pricing for each plan. Set 0 for unlimited.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
        {plans.map((plan) => (
          <div key={plan.id} className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{plan.name}</h3>
                <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>Key: {plan.key}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={plan.is_active}
                    onChange={(e) => updateField(plan.id, 'is_active', e.target.checked)}
                  />
                  Active
                </label>
                <button
                  onClick={() => handleDeletePlan(plan)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: 4 }}
                  title="Delete plan"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Plan Name</label>
                <input style={inputStyle} value={plan.name} onChange={(e) => updateField(plan.id, 'name', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Price (KWD)</label>
                <input style={inputStyle} type="number" step="0.001" value={plan.price} onChange={(e) => updateField(plan.id, 'price', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Max Stores <span style={{ color: 'var(--gray-400)' }}>(0=∞)</span></label>
                <input style={inputStyle} type="number" min="0" value={plan.max_stores} onChange={(e) => updateField(plan.id, 'max_stores', parseOptionalInt(e.target.value))} />
              </div>
              <div>
                <label style={labelStyle}>Max Branches <span style={{ color: 'var(--gray-400)' }}>(0=∞)</span></label>
                <input style={inputStyle} type="number" min="0" value={plan.max_branches} onChange={(e) => updateField(plan.id, 'max_branches', parseOptionalInt(e.target.value))} />
              </div>
              <div>
                <label style={labelStyle}>Max Products <span style={{ color: 'var(--gray-400)' }}>(0=∞)</span></label>
                <input style={inputStyle} type="number" min="0" value={plan.max_products} onChange={(e) => updateField(plan.id, 'max_products', parseOptionalInt(e.target.value))} />
              </div>
              <div>
                <label style={labelStyle}>Free Domain Suffix</label>
                <input style={inputStyle} value={plan.free_domain_suffix || ''} onChange={(e) => updateField(plan.id, 'free_domain_suffix', e.target.value)} placeholder="e.g. cartz.cc" />
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={plan.custom_domain} onChange={(e) => updateField(plan.id, 'custom_domain', e.target.checked)} />
              Allow Custom Domain
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!plan.pos_enabled} onChange={(e) => updateField(plan.id, 'pos_enabled', e.target.checked)} />
              Include Point of Sale
            </label>

            <button
              className="btn btn-primary"
              style={{ width: '100%', marginTop: 16 }}
              onClick={() => savePlan(plan)}
              disabled={saving === plan.id}
            >
              <FiSave style={{ marginInlineEnd: 6 }} />
              {saving === plan.id ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ))}
      </div>

      {/* Add Plan Modal */}
      {showAdd && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !adding && setShowAdd(false)}
        >
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 460, width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: 20 }}>Add New Plan</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Plan Key <span style={{ color: '#ef4444' }}>*</span></label>
                <input style={inputStyle} value={newPlan.key} onChange={(e) => setNewPlan({ ...newPlan, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} placeholder="e.g. premium" />
              </div>
              <div>
                <label style={labelStyle}>Plan Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input style={inputStyle} value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} placeholder="e.g. Premium" />
              </div>
              <div>
                <label style={labelStyle}>Price (KWD)</label>
                <input
                  style={inputStyle}
                  type="number"
                  step="0.001"
                  value={newPlan.price}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setNewPlan({ ...newPlan, price: raw === '' ? ('' as any) : (Number.isNaN(parseFloat(raw)) ? ('' as any) : parseFloat(raw)) });
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>Max Stores (0=∞)</label>
                <input style={inputStyle} type="number" min="0" value={newPlan.max_stores} onChange={(e) => setNewPlan({ ...newPlan, max_stores: parseOptionalInt(e.target.value) as any })} />
              </div>
              <div>
                <label style={labelStyle}>Max Branches (0=∞)</label>
                <input style={inputStyle} type="number" min="0" value={newPlan.max_branches} onChange={(e) => setNewPlan({ ...newPlan, max_branches: parseOptionalInt(e.target.value) as any })} />
              </div>
              <div>
                <label style={labelStyle}>Max Products (0=∞)</label>
                <input style={inputStyle} type="number" min="0" value={newPlan.max_products} onChange={(e) => setNewPlan({ ...newPlan, max_products: parseOptionalInt(e.target.value) as any })} />
              </div>
              <div>
                <label style={labelStyle}>Free Domain Suffix</label>
                <input style={inputStyle} value={newPlan.free_domain_suffix} onChange={(e) => setNewPlan({ ...newPlan, free_domain_suffix: e.target.value })} placeholder="cartz.cc" />
              </div>
              <div style={{ display: 'flex', alignItems: 'end', paddingBottom: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={newPlan.custom_domain} onChange={(e) => setNewPlan({ ...newPlan, custom_domain: e.target.checked })} />
                  Custom Domain
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginTop: 8 }}>
                  <input type="checkbox" checked={newPlan.pos_enabled} onChange={(e) => setNewPlan({ ...newPlan, pos_enabled: e.target.checked })} />
                  Include POS
                </label>
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: 20 }}
              onClick={handleAddPlan}
              disabled={adding}
            >
              {adding ? 'Creating...' : 'Create Plan'}
            </button>
            <button
              style={{ width: '100%', marginTop: 8, padding: 10, background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer' }}
              onClick={() => setShowAdd(false)}
              disabled={adding}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--gray-600)',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--gray-200)',
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};
